import type { ProseMirrorContent } from "@shared/models/card-stack"
import { headlessCardStackSchema } from "@shared/unified-tiptap/extensions"
import { createUntitledCard } from "@shared/unified-tiptap/util/card"
import type { Editor, JSONContent } from "@tiptap/core"
import { Node as PMNode } from "@tiptap/pm/model"
import { IdList } from "articulated"
import { nanoid } from "nanoid"

/**
 * Returns the ProseMirror doc corresponding to an empty CardStack (excluding the lesson title).
 */
export function emptyCardStackDoc(): PMNode {
  const schema = headlessCardStackSchema()
  const doc = schema.node("doc", null, [])
  return doc
}

/**
 * Returns ProseMirrorContent corresponding to an empty CardStack (excluding the lesson title).
 */
export function emptyProseMirrorContent(): ProseMirrorContent {
  const doc = emptyCardStackDoc()
  const idList = initialIdList(doc)
  return { json: doc.toJSON(), ids: idList.save() }
}

/**
 * Returns ProseMirrorContent for a CardStack holding a non-instructional lesson's
 * note: one empty untitledCard. Also returns the cardId for analytics.
 */
export function nonInstructionalNoteInitialContent(): {
  content: ProseMirrorContent
  cardId: string
} {
  const schema = headlessCardStackSchema()
  const card = createUntitledCard(schema)
  const doc = schema.node("doc", null, [card])
  return {
    content: { json: doc.toJSON(), ids: initialIdList(doc).save() },
    cardId: card.attrs.id as string,
  }
}

/**
 * The fields in a CardStack that are managed by the Editor in
 * UnifiedCardStackEditor/UnifiedLessonSimpleReadOnly.
 */
export interface EditorCardStackState {
  /** The lesson title. */
  title: string
  /** The ProseMirror content (JSON + articulated IDs) excluding the lesson title. */
  content: ProseMirrorContent
}

/**
 * Returns JSONContent for the given EditorCardStackState, suitable
 * for passing to `editor.setContent`.
 *
 * Invert with getEditorCardStackState.
 */
export function getEditorJsonContent(
  state: EditorCardStackState,
  options: { isUntitled?: boolean } = {},
): JSONContent {
  if (options.isUntitled) {
    return state.content.json
  } else {
    return {
      type: "doc",
      content: [
        {
          type: "lessonTitle",
          content: state.title ? [{ type: "text", text: state.title }] : undefined,
        },
        ...(state.content.json.content ?? []),
      ],
    }
  }
}

/**
 * Returns the current EditorCardStackState of the given editor.
 *
 * Inverse of getEditorJsonContent.
 */
export function getEditorCardStackState(
  editor: Editor,
  options: { isUntitled?: boolean } = {},
): EditorCardStackState {
  let title: string
  let contentJson: JSONContent

  if (options.isUntitled) {
    title = ""
    contentJson = editor.getJSON()
  } else {
    title = editor.state.doc.firstChild?.textContent ?? ""
    contentJson = {
      type: "doc",
      content: (editor.getJSON().content ?? []).slice(1),
    }
  }

  // TODO: Actual tracking.
  const idList = initialIdList(parseCardStackProseMirrorJson(contentJson))
  return { title, content: { json: contentJson, ids: idList.save() } }
}

/**
 * Returns an initial IdList appropriate for the given content.
 *
 * The list has a single bunch with length parse(json).content.size and a random bunchId.
 * The bunchId will be *different each call*, so avoid calling this more than once
 * for the same content (e.g. on different renders).
 */
export function initialIdList(doc: PMNode): IdList {
  const idCount = doc.content.size
  return IdList.new().insertAfter(null, { bunchId: nextBunchId(), counter: 0 }, idCount)
}

/**
 * Make the clientId small while still extremely likely to avoid collisions, given that
 * (a) we're only generating it once per tab, and
 * (b) bunchIds don't strictly need to be globally unique, just unique within each lesson.
 *
 * Each char gives 6 bits of entropy (alphabet size 64), so this is 60 bits,
 * enough to comfortably avoid the birthday paradox with 10,000,000s of clientIds.
 * For comparison, Yjs clientIds have 32 bits of entropy, while UUIDv4s have 122.
 */
const clientId = nanoid(10)
let clientCounter = 0

/**
 * Returns a short, globally-unique ID usable as a bunchId.
 * Pass this to `new ElementIdGenerator(nextBunchId)`.
 *
 * BunchId format: "\<unique client id\>:\<client counter\>".
 * The client counter is base-36 encoded. Note that this format is variable-length.
 */
export function nextBunchId(): string {
  return clientId + ":" + (clientCounter++).toString(36)
}

/**
 * Parses CardStack.proseMirror.json as ProseMirror document.
 */
export function parseCardStackProseMirrorJson(json: JSONContent): PMNode {
  return headlessCardStackSchema().nodeFromJSON(json)
}

/**
 * Concatenates content structs, stacking their cards.
 */
export function concatProseMirrorContent(...contents: ProseMirrorContent[]): ProseMirrorContent {
  // ProseMirror doc: concatenate the input docs' children (cards).
  const schema = headlessCardStackSchema()
  const doc = schema.node(
    "doc",
    null,
    contents.flatMap((content) => schema.nodeFromJSON(content.json).children),
  )
  // TODO: actual IdList handling
  const idList = initialIdList(doc)
  return { json: doc.toJSON(), ids: idList.save() }
}
